import {
  ELessonPlanStatus,
  ILessonPlan,
  EActivityType,
  ECompetencyType,
  ELearningOutcomeLevel,
  EAssessmentMethod,
  EMultipleIntelligence,
} from 'src/apis/academics/academicsLessonPlans.interfaces';
import { mockUserAction } from '../users/userAction';
import { EAcademicBoard } from 'src/apis/academics/academicsClassesAndCourses.interfaces';
import { mockAcademicsClasses } from './academicsClasses.mock';
import { mockAcademicsCourses } from './academicsCourses.mock';

// Indian teacher names
const indianTeacherNames = [
  'Priya Sharma', 'Rajesh Kumar', 'Anjali Patel', 'Vikram Singh', 'Kavita Reddy',
  'Ramesh Agarwal', 'Meera Iyer', 'Amit Gupta', 'Sunita Joshi', 'Suresh Nair',
  'Neha Rao', 'Karan Desai', 'Radha Shah', 'Anil Verma', 'Shreya Agarwal',
  'Manoj Sharma', 'Divya Patel', 'Deepak Kumar', 'Pooja Singh', 'Ravi Reddy',
  'Kiran Mehta', 'Nisha Gupta', 'Sandeep Joshi', 'Uma Iyer', 'Gopal Nair',
  'Lakshmi Rao', 'Venkat Desai', 'Sarita Shah', 'Rohit Verma', 'Aditi Agarwal',
  'Nikhil Sharma', 'Swati Patel', 'Kunal Kumar', 'Richa Singh', 'Varun Reddy',
  'Tanvi Mehta', 'Harsh Gupta', 'Isha Joshi', 'Yash Iyer', 'Aryan Nair',
  'Ananya Rao', 'Dhruv Desai', 'Ishita Shah', 'Rohan Verma', 'Siddharth Agarwal',
  'Tanya Sharma', 'Vivek Patel', 'Zara Kumar', 'Aarav Singh', 'Ritika Agarwal',
  'Kartik Sharma', 'Meghna Patel', 'Sanjana Singh', 'Vedant Reddy', 'Ishani Mehta',
];

// Subject topics for different subjects
const subjectTopics: Record<string, string[]> = {
  Mathematics: [
    'Algebra Fundamentals', 'Quadratic Equations', 'Trigonometry Basics', 'Geometry - Triangles',
    'Statistics and Probability', 'Linear Equations', 'Number Systems', 'Polynomials',
    'Coordinate Geometry', 'Mensuration', 'Arithmetic Progressions', 'Circles',
  ],
  English: [
    'Poetry Analysis', 'Grammar - Tenses', 'Reading Comprehension', 'Creative Writing',
    'Literature - Prose', 'Vocabulary Building', 'Essay Writing', 'Letter Writing',
    'Story Writing', 'Report Writing', 'Debate Skills', 'Public Speaking',
  ],
  Science: [
    'Chemical Reactions', 'Force and Motion', 'Cell Structure', 'Photosynthesis',
    'Electricity and Magnetism', 'Acids and Bases', 'Reproduction', 'Ecosystem',
    'Light and Reflection', 'Sound Waves', 'Matter and States', 'Food Chain',
  ],
  'Social Studies': [
    'Indian National Movement', 'Indian Constitution', 'Medieval India', 'Ancient Civilizations',
    'Physical Geography', 'Climate and Weather', 'Democracy', 'Economic Development',
    'Rivers and Mountains', 'Freedom Struggle', 'World Wars', 'Indian Culture',
  ],
  Physics: [
    'Mechanics', 'Thermodynamics', 'Optics', 'Electromagnetism', 'Waves and Oscillations',
    'Modern Physics', 'Nuclear Physics', 'Semiconductors', 'Gravitation', 'Work and Energy',
  ],
  Chemistry: [
    'Organic Chemistry', 'Inorganic Chemistry', 'Chemical Bonding', 'Periodic Table',
    'Stoichiometry', 'Thermodynamics', 'Electrochemistry', 'Equilibrium', 'Kinetics',
  ],
  Biology: [
    'Cell Biology', 'Genetics', 'Evolution', 'Ecology', 'Human Physiology',
    'Plant Physiology', 'Microbiology', 'Biotechnology', 'Anatomy', 'Zoology',
  ],
};

const statuses = [
  ELessonPlanStatus.DRAFT,
  ELessonPlanStatus.IN_REVIEW,
  ELessonPlanStatus.APPROVED,
  ELessonPlanStatus.ACTIVE,
  ELessonPlanStatus.ARCHIVED,
];

function generateLessonPlan(index: number): ILessonPlan {
  const classIndex = index % mockAcademicsClasses.length;
  const classData = mockAcademicsClasses[classIndex];
  const courseIndex = index % mockAcademicsCourses.length;
  const courseData = mockAcademicsCourses[courseIndex];
  
  const subject = courseData.name.split(' - ')[0] || 'Mathematics';
  const topics = subjectTopics[subject] || subjectTopics['Mathematics'];
  const topic = topics[index % topics.length];
  
  const status = statuses[index % statuses.length];
  const teacherId = courseData.teacherId || (index % 50) + 1;
  const teacherName = indianTeacherNames[teacherId % indianTeacherNames.length];
  
  const gradeLevel = classData.name.replace('Class ', '').replace(' Section A', '').replace(' Section B', '').replace(' Science A', '').replace(' Science B', '').replace(' Commerce', '').replace(' Arts', '').replace(' Vocational', '');
  
  const unitId = `unit-${index + 1}`;
  const sessionId = `session-${index + 1}`;
  const objId = `obj-${index + 1}`;
  const loId = `lo-${index + 1}`;
  const assessId = `assess-${index + 1}`;
  const actId = `act-${index + 1}`;
  const exId = `ex-${index + 1}`;
  
  const duration = 10 + (index % 15);
  const startDate = new Date(2024, 3, 1 + (index % 30));
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + duration);
  
  return {
    id: String(index + 1),
    title: `${topic} - ${classData.name}`,
    description: `Comprehensive lesson plan covering ${topic.toLowerCase()} aligned with ${classData.board || 'CBSE'} curriculum for ${classData.name}. Prepared by ${teacherName}.`,
    academicYear: '2024-2025',
    board: classData.board || EAcademicBoard.CBSE,
    classId: classData.id,
    courseId: courseData.id,
    teacherId,
    curriculumFramework: classData.board === EAcademicBoard.ICSE ? 'ICSE' : 'CBSE',
    syllabusReference: `Chapter ${(index % 10) + 1}: ${topic}`,
    textbookReference: `NCERT ${classData.name}, Chapter ${(index % 10) + 1}, Pages ${(index % 50) + 1}-${(index % 50) + 20}`,
    ncertChapter: `Chapter ${(index % 10) + 1}`,
    status,
    version: (index % 3) + 1,
    approvedBy: status === ELessonPlanStatus.APPROVED || status === ELessonPlanStatus.ACTIVE ? 1 : undefined,
    approvedAt: status === ELessonPlanStatus.APPROVED || status === ELessonPlanStatus.ACTIVE ? new Date().toISOString() : undefined,
    tags: [subject.toLowerCase(), topic.toLowerCase().replace(/\s+/g, '-'), 'cbse'],
    subject,
    gradeLevel,
    totalDuration: duration,
    units: [
      {
        id: unitId,
        unitNumber: 1,
        title: `Introduction to ${topic}`,
        description: `Understanding ${topic.toLowerCase()} concepts and applications`,
        duration,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        sessions: [
          {
            id: sessionId,
            sessionNumber: 1,
            title: `${topic} Basics`,
            duration: 45,
            objectives: [objId],
            activities: [actId],
            examples: [exId],
            strategies: 'Interactive discussion with real-world examples and hands-on activities',
            closure: 'Summary of key concepts and quick assessment',
            homework: `Practice problems 1-10 from textbook chapter ${(index % 10) + 1}`,
          },
        ],
        learningObjectives: [
          {
            id: objId,
            objective: `Students will be able to understand and apply ${topic.toLowerCase()} concepts`,
            competencyType: ECompetencyType.KNOWLEDGE,
            learningOutcomeLevel: ELearningOutcomeLevel.UNDERSTAND,
            isPrimary: true,
          },
        ],
        learningOutcomes: [
          {
            id: loId,
            code: `LO-${(index % 10) + 1}.1`,
            description: `Understand the concept of ${topic.toLowerCase()} and its applications`,
            level: ELearningOutcomeLevel.UNDERSTAND,
            competencyType: ECompetencyType.KNOWLEDGE,
            mappedToObjectiveIds: [objId],
          },
        ],
        assessments: [
          {
            id: assessId,
            competencyType: ECompetencyType.KNOWLEDGE,
            assessmentMethod: EAssessmentMethod.WRITTEN_TEST,
            description: `Class test on ${topic.toLowerCase()}`,
            criteria: 'Correct understanding and application of concepts',
            weightage: 20 + (index % 20),
            duration: 30 + (index % 30),
          },
        ],
        differentiationStrategies: [],
      },
    ],
    activities: [
      {
        id: actId,
        type: EActivityType.HOOK,
        title: `${topic} Discovery`,
        description: `Students explore real-world scenarios involving ${topic.toLowerCase()}`,
        duration: 15 + (index % 15),
        materials: ['Whiteboard', 'Markers', 'Reference materials'],
        instructions: 'Present scenarios and ask students to identify patterns and concepts',
        expectedOutcome: `Students recognize ${topic.toLowerCase()} patterns in daily life`,
        multipleIntelligences: [
          EMultipleIntelligence.LOGICAL_MATHEMATICAL,
          EMultipleIntelligence.SPATIAL,
        ],
      },
    ],
    examples: [
      {
        id: exId,
        title: `${topic} Example`,
        description: `Practical example demonstrating ${topic.toLowerCase()}`,
        context: `Real-world application of ${topic.toLowerCase()} concepts`,
        relevance: `Demonstrates ${topic.toLowerCase()} in practical context`,
        source: `NCERT Textbook Example ${(index % 10) + 1}.${(index % 5) + 1}`,
      },
    ],
    actions: {
      created: mockUserAction,
      ...(status === ELessonPlanStatus.APPROVED || status === ELessonPlanStatus.ACTIVE ? { updated: mockUserAction } : {}),
    },
  };
}

export const mockAcademicsLessonPlanList: ILessonPlan[] = Array.from({ length: 60 }, (_, i) => generateLessonPlan(i));
